import { Stack, Text, Checkbox, SimpleGrid, Group, Chip, Tooltip, ActionIcon } from '@mantine/core';
import { useState, forwardRef, useImperativeHandle } from 'react';
import { IconInfoCircle } from '@tabler/icons-react';
import { FoodAutocompleteInput } from '../UI/FoodAutocompleteInput';
import { searchIngredients } from '../../data/ingredientDatabase';
import {
  DIETARY_RESTRICTIONS,
  NUTRITION_GOALS,
  NUTRITION_GOAL_PRESETS,
  COMMON_ALLERGIES,
  RESTRICTION_TOOLTIPS,
  hasExclusionConflict,
  isGoalPresetActive,
  toggleGoalPreset,
} from '../../utils/dietaryOptions';
import { useDietarySelection } from '../../hooks/useDietarySelection';

type DietaryPreferenceProps = {
  value: { dietaryRestrictions: string[]; allergies: string[] };
  onChange: (value: { dietaryRestrictions: string[]; allergies: string[] }) => void;
};

export type DietaryPreferenceRef = {
  flushInputs: () => void;
};

const DietaryPreferenceComponent = forwardRef<DietaryPreferenceRef, DietaryPreferenceProps>(
  ({ value, onChange }, ref) => {
    const [allergyInput, setAllergyInput] = useState('');

    const {
      toggleAllergy,
      addAllergiesFromText,
      removeAllergy,
      toggleDietaryRestriction,
      setDietaryRestrictions,
    } = useDietarySelection(value, onChange);

    useImperativeHandle(ref, () => ({
      flushInputs: () => {
        if (allergyInput.trim() && addAllergiesFromText(allergyInput)) {
          setAllergyInput('');
        }
      }
    }));

  const addAllergy = (text: string) => {
    if (addAllergiesFromText(text)) {
      setAllergyInput('');
    }
  };

  const predefinedAllergies = value.allergies.filter(a => COMMON_ALLERGIES.includes(a));
  const customAllergies = value.allergies.filter(a => !COMMON_ALLERGIES.includes(a));

  // Ingredient-catalog suggestions for the "Other allergies" input (same catalog
  // that powers pantry autocomplete). Free text is always allowed since many
  // allergens (e.g. sulfites) aren't ingredients. Search once there are 2+
  // characters, and hide anything already selected.
  const allergySuggestions = allergyInput.trim().length >= 2
    ? searchIngredients(allergyInput, 8)
        .map(ing => ing.displayName ?? ing.name)
        .filter(name => !value.allergies.some(a => a.toLowerCase() === name.toLowerCase()))
    : [];

  // Conflict dimming evaluates against the COMBINED selection because
  // exclusion groups span sections (Keto vs the fat goals, High-Fiber vs
  // Low-Residue).
  const renderOptionGrid = (options: string[]) => (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      {options.map((option) => {
        const isSelected = value.dietaryRestrictions.includes(option);
        const hasConflict = hasExclusionConflict(value.dietaryRestrictions, option);
        const tooltip = RESTRICTION_TOOLTIPS[option];

        const checkbox = (
          <Checkbox
            key={tooltip ? undefined : option}
            label={option}
            checked={isSelected}
            onChange={() => toggleDietaryRestriction(option)}
            size="sm"
            color="orange"
            style={{ opacity: hasConflict ? 0.5 : 1 }}
            styles={{
              root: { cursor: 'pointer' },
              input: { cursor: 'pointer' },
              label: { cursor: 'pointer' }
            }}
          />
        );

        if (!tooltip) {
          return checkbox;
        }

        return (
          <Group key={option} gap="xs" wrap="nowrap">
            {checkbox}
            <Tooltip
              label={tooltip}
              multiline
              w={220}
              withArrow
              events={{ hover: true, focus: true, touch: true }}
            >
              <ActionIcon size="xs" variant="subtle" color="gray">
                <IconInfoCircle size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        );
      })}
    </SimpleGrid>
  );

  return (
    <Stack gap="lg">
      {/* Dietary Restrictions */}
      <Stack gap="sm">
        <Text fw={500}>Dietary restrictions</Text>
        {renderOptionGrid(DIETARY_RESTRICTIONS)}
      </Stack>

      {/* Nutrition Goals */}
      <Stack gap="sm">
        <Text fw={500}>Nutrition goals</Text>

        {/* Quick-pick chips with derived state: a chip is on iff every goal
            in its bundle is checked below — the checkboxes stay the source of
            truth. Toggling on completes the bundle; toggling off unchecks it. */}
        <Group gap="xs" wrap="wrap" align="center">
          {NUTRITION_GOAL_PRESETS.map((preset) => (
            <Chip
              key={preset.key}
              checked={isGoalPresetActive(value.dietaryRestrictions, preset.goals)}
              onChange={() =>
                setDietaryRestrictions(
                  toggleGoalPreset(value.dietaryRestrictions, preset.goals)
                )
              }
              color="orange"
              size="xs"
            >
              {preset.label}
            </Chip>
          ))}
        </Group>

        {renderOptionGrid(NUTRITION_GOALS)}
      </Stack>

      {/* Allergies */}
      <Stack gap="sm">
        <Text fw={500}>Food allergies</Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {COMMON_ALLERGIES.map((allergy) => (
            <Checkbox
              key={allergy}
              label={allergy}
              checked={predefinedAllergies.includes(allergy)}
              onChange={() => toggleAllergy(allergy)}
              size="sm"
              color="red"
              styles={{
                root: { cursor: 'pointer' },
                input: { cursor: 'pointer' },
                label: { cursor: 'pointer' }
              }}
            />
          ))}
        </SimpleGrid>

        {/* Other allergies input */}
        <Stack gap="xs">
          <Text fw={500} size="sm">Other allergies</Text>
          <FoodAutocompleteInput
            value={allergyInput}
            onChange={setAllergyInput}
            onAdd={addAllergy}
            suggestions={allergySuggestions}
            placeholder="e.g. sulfites, celery, corn..."
            color="red"
          />

          {/* Custom allergies display */}
          {customAllergies.length > 0 && (
            <Group gap="xs" wrap="wrap" className="chip-cloud">
              {customAllergies.map(allergy => (
                <Chip
                  key={allergy}
                  checked={true}
                  onChange={() => removeAllergy(allergy)}
                  variant="filled"
                  color="red"
                  size="sm"
                >
                  {allergy}
                </Chip>
              ))}
            </Group>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});

DietaryPreferenceComponent.displayName = 'DietaryPreference';

export const DietaryPreference = DietaryPreferenceComponent;
